import React from 'react';

import { Moment } from 'moment';
import { createSelector } from 'reselect';

import { useMediaQuery, useTheme } from '@material-ui/core';

import { FeatureFlagged, FeatureGatedVariant } from '../../../feature-gates/feature-flagged';
import { FeatureGates } from '../../../feature-gates/feature-gates';
import { OnMessageSendInput } from '../../../typescript-types/messages/on-message-send-type';
import { LegacyMultiLanguageMessageComposer } from '../legacy-multiple-language-message-composer';
import { MultiLanguageMessageComposer } from '../multiple-language-message-composer';

import { LanguageCode } from '../../../util/languages';
import { MessageType } from '../../../util/message-types';

type SendMessageFunction = (
  message: string,
  messageType: string,
  subject: string,
  sendMessageAt: Moment | null,
  optionalFields?: {
    urlFileMappings?: Array<UrlFileMapping>;
    sendWithVCard?: boolean;
  },
) => Promise<any>;

export type Props = {
  orgId?: number;
  onSendMessage?: SendMessageFunction;
  enableEmail: boolean;
  enableText: boolean;

  message?: string | null;
  autofocus?: boolean;
  initialType?: MessageType;
  subject?: string;
  onSubjectChange?: (subject: string) => void;
  onMessageSent?: () => void;
  onMessageTypeChange?: (type: MessageType) => void;
  onError?: (response: any) => void;
  onChange?: (message: string) => void;
  handleToggleTemplateDialog?: () => void;
  buttonText?: string;
  disabled?: boolean;
  canScheduleMessageInFuture?: boolean;
  canPersonalize?: boolean;
  onUploadFile?: (file: File) => Promise<string>;
  urlFileMappings?: Array<UrlFileMapping>;
  onUrlFileDelete?: (urlFileMapping: UrlFileMapping) => () => void;
  canUploadFile?: boolean;
  clearDraft?: () => void;
  allowSendWithVCard?: boolean;
  canTranslate?: boolean;
};

// IF (09/29/20): Need to use a selector here so we're not creating a new languageToMessage or
// languageToSubject object everytime this component updates. That causes the
// MultiLanguageMessageComposer to think the message is constantly changing resulting in an infinite loop.
export const languageToMessageSelector = createSelector(
  (message: string | null | undefined) => message,
  (message) => ({
    en: message ?? '',
  }),
);

export const languageToSubjectSelector = createSelector(
  (subject: string | null | undefined) => subject,
  (subject) => ({
    en: subject ?? '',
  }),
);

export const SingleLanguageMessageComposer = (props: Props) => {
  const { onSendMessage, onSubjectChange, onChange, handleToggleTemplateDialog, canUploadFile } =
    props;

  const theme = useTheme();
  const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const handleMultiLanguageSendMessage = onSendMessage
    ? (onMessageSendInput: OnMessageSendInput) =>
        onSendMessage(
          onMessageSendInput.languageToMessage.en,
          onMessageSendInput.messageType,
          onMessageSendInput.languageToSubject.en,
          onMessageSendInput.sendMessageAt,
          {
            urlFileMappings: onMessageSendInput.urlFileMappings,
            sendWithVCard: onMessageSendInput.sendWithVCard,
          },
        )
    : undefined;
  const handleMultiLanguageMessageChange = onChange
    ? (languageToMessage: Record<LanguageCode, string>) => onChange(languageToMessage.en)
    : undefined;
  const handleMultiLanguageSubjectChange = onSubjectChange
    ? (languageToSubject: Record<LanguageCode, string>) => onSubjectChange(languageToSubject.en)
    : undefined;
  const languageToMessage = languageToMessageSelector(props.message);
  const languageToSubject = props.subject ? languageToSubjectSelector(props.subject) : undefined;

  return (
    <>
      <FeatureFlagged
        variant={FeatureGatedVariant.HIDE_ON_FLAG}
        flagName={FeatureGates.INTEGRATED_MESSAGING}
      >
        <LegacyMultiLanguageMessageComposer
          orgId={props.orgId}
          onSendMessage={handleMultiLanguageSendMessage}
          enableEmail={props.enableEmail}
          enableText={props.enableText}
          canPersonalize={props.canPersonalize}
          languageToMessage={languageToMessage}
          languageToSubject={languageToSubject}
          autofocus={props.autofocus}
          initialType={props.initialType}
          onSubjectChange={handleMultiLanguageSubjectChange}
          onMessageSent={props.onMessageSent}
          onMessageTypeChange={props.onMessageTypeChange}
          onError={props.onError}
          onChange={handleMultiLanguageMessageChange}
          buttonText={props.buttonText}
          disabled={props.disabled}
          canScheduleMessageInFuture={props.canScheduleMessageInFuture}
          canUploadFile={canUploadFile}
          clearDraft={props.clearDraft}
          allowSendWithVCard={props.allowSendWithVCard}
          canTranslate={props.canTranslate}
        />
      </FeatureFlagged>
      <FeatureFlagged
        variant={FeatureGatedVariant.SHOW_ON_FLAG}
        flagName={FeatureGates.INTEGRATED_MESSAGING}
      >
        <MultiLanguageMessageComposer
          orgId={props.orgId}
          onSendMessage={handleMultiLanguageSendMessage}
          enableEmail={props.enableEmail}
          enableText={props.enableText}
          canPersonalize={props.canPersonalize}
          languageToMessage={languageToMessage}
          languageToSubject={languageToSubject}
          autofocus={props.autofocus}
          initialType={props.initialType}
          onSubjectChange={handleMultiLanguageSubjectChange}
          onMessageSent={props.onMessageSent}
          onMessageTypeChange={props.onMessageTypeChange}
          onError={props.onError}
          onChange={handleMultiLanguageMessageChange}
          buttonText={props.buttonText}
          disabled={props.disabled}
          canScheduleMessageInFuture={props.canScheduleMessageInFuture}
          canUploadFile={canUploadFile}
          clearDraft={props.clearDraft}
          allowSendWithVCard={props.allowSendWithVCard}
          canTranslate={props.canTranslate}
          handleToggleTemplateDialog={handleToggleTemplateDialog}
          isOnMobile={isOnMobile}
        />
      </FeatureFlagged>
    </>
  );
};
